import {
  Check,
  ChevronDown,
  Columns3,
  Download,
  Filter,
  Link2,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import type { Dispatch, KeyboardEvent, ReactNode, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client.js";
import type {
  AnalysisFamily,
  EvidenceRecord,
  FacetValue,
  FindingAgeBand,
  FindingBusinessTier,
  FindingEnrichmentState,
  FindingFacets,
  FindingPrivateReadState,
  FindingQueryState,
  FindingRemediationState,
  FindingSlaState,
  FindingStatus,
  Severity,
  ValidationStage,
} from "../../api/types.js";
import { displayRuleId } from "../../app/rule-id.js";
import { downloadExport } from "../../lib/download.js";
import { formatInteger, titleCase } from "../../lib/format.js";
import type { ColumnKey } from "./FindingsTable.js";
import { COLUMN_DEFINITIONS } from "./FindingsTable.js";
import {
  ACTIONABLE_STATUSES,
  AGE_BANDS,
  ANALYSIS_FAMILIES,
  ANALYSIS_FAMILY_LABELS,
  BUSINESS_TIERS,
  countAdvancedFilters,
  ENRICHMENT_STATES,
  FINDING_STATUSES,
  filtersForExport,
  hasAnyFilter,
  PRIVATE_READ_STATES,
  REMEDIATION_STATES,
  REPLAY_STATUSES,
  SEVERITIES,
  SLA_STATES,
  statusesForPreset,
  toggleOrderedValue,
  VALIDATION_DEPTHS,
} from "./model.js";

function CheckboxMenu<T extends string | number>({
  label,
  selected,
  options,
  onChange,
  search = "",
  renderValue = (value) => titleCase(String(value)),
}: {
  label: string;
  selected: readonly T[];
  options: readonly FacetValue<T>[];
  onChange: (values: T[]) => void;
  search?: string;
  renderValue?: (value: T) => string;
}) {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleOptions = normalizedSearch
    ? options.filter((option) =>
        (option.label || renderValue(option.value)).toLocaleLowerCase().includes(normalizedSearch),
      )
    : options;
  return (
    <details className="menu-popover filter-menu">
      <summary className="control-button">
        <span>{label}</span>
        {selected.length > 0 ? <span className="count-chip">{selected.length}</span> : null}
        <ChevronDown aria-hidden="true" size={14} />
      </summary>
      <div className="menu-popover__panel filter-menu__panel">
        <div className="filter-menu__heading">
          <strong>{label}</strong>
          {selected.length > 0 ? (
            <button className="text-button" type="button" onClick={() => onChange([])}>
              Clear
            </button>
          ) : null}
        </div>
        <div className="filter-menu__options">
          {visibleOptions.map((option) => (
            <label className="check-option" key={String(option.value)}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() =>
                  onChange(
                    selected.includes(option.value)
                      ? selected.filter((value) => value !== option.value)
                      : [...selected, option.value],
                  )
                }
              />
              <span className="check-option__box" aria-hidden="true">
                <Check size={12} />
              </span>
              <span>{option.label || renderValue(option.value)}</span>
              <span className="check-option__count">{formatInteger(option.count)}</span>
            </label>
          ))}
          {visibleOptions.length === 0 ? (
            <span className="filter-menu__empty">
              {normalizedSearch ? "No matching options" : "No options available"}
            </span>
          ) : null}
        </div>
      </div>
    </details>
  );
}

function staticOptions<T extends string | number>(values: readonly T[]): FacetValue<T>[] {
  return values.map((value) => ({ value, label: titleCase(String(value)), count: 0 }));
}

function publicEngineLabel(value: string): string {
  return value === "codex-security" ? "Independent AI review" : value;
}

function FilterStudioColumn({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <section className="filter-studio__column">
      <header>
        <h3>{title}</h3>
        <p>{hint}</p>
      </header>
      <div className="filter-studio__fields">{children}</div>
    </section>
  );
}

function advancedChipItems(
  state: FindingQueryState,
  update: (patch: Partial<FindingQueryState>) => void,
): { key: string; label: string; onClear: () => void }[] {
  const chips: { key: string; label: string; onClear: () => void }[] = [];
  const pushList = (key: string, count: number, label: string, onClear: () => void) => {
    if (count > 0) {
      chips.push({
        key,
        label: count === 1 ? label : `${label} · ${count}`,
        onClear,
      });
    }
  };
  if (state.validationDepths.length > 0) {
    chips.push({
      key: "validation",
      label: `Validation ${state.validationDepths.map((depth) => `${depth}V`).join(" ")}`,
      onClear: () => update({ validationDepths: [] }),
    });
  }
  pushList("repositoryIds", state.repositoryIds.length, "Repository", () =>
    update({ repositoryIds: [] }),
  );
  pushList("businessTiers", state.businessTiers.length, "Business tier", () =>
    update({ businessTiers: [] }),
  );
  pushList("componentKeys", state.componentKeys.length, "Component", () =>
    update({ componentKeys: [] }),
  );
  pushList("engineIds", state.engineIds.length, "Engine", () => update({ engineIds: [] }));
  pushList("assigneeIds", state.assigneeIds.length, "Assignee", () => update({ assigneeIds: [] }));
  pushList("slaStates", state.slaStates.length, "SLA", () => update({ slaStates: [] }));
  pushList("enrichmentStates", state.enrichmentStates.length, "Enrichment", () =>
    update({ enrichmentStates: [] }),
  );
  pushList("remediationStates", state.remediationStates.length, "Remediation", () =>
    update({ remediationStates: [] }),
  );
  pushList("privateReadStates", state.privateReadStates.length, "Read state", () =>
    update({ privateReadStates: [] }),
  );
  pushList("privateCategoryKeys", state.privateCategoryKeys.length, "My categories", () =>
    update({ privateCategoryKeys: [] }),
  );
  pushList("ageBands", state.ageBands.length, "Age", () => update({ ageBands: [] }));
  pushList("ruleIds", state.ruleIds.length, "Rule", () => update({ ruleIds: [] }));
  pushList("cwes", state.cwes.length, "CWE", () => update({ cwes: [] }));
  pushList("owaspCategories", state.owaspCategories.length, "OWASP", () =>
    update({ owaspCategories: [] }),
  );
  pushList("languages", state.languages.length, "Language", () => update({ languages: [] }));
  pushList("replayStatuses", state.replayStatuses.length, "Replay", () =>
    update({ replayStatuses: [] }),
  );
  if (state.minConfidence > 0) {
    chips.push({
      key: "confidence",
      label: `Confidence ≥ ${state.minConfidence}%`,
      onClear: () => update({ minConfidence: 0 }),
    });
  }
  return chips;
}

function ColumnChooser({
  visibleColumns,
  onToggle,
  onReset,
}: {
  visibleColumns: ReadonlySet<ColumnKey>;
  onToggle: (column: ColumnKey) => void;
  onReset: () => void;
}) {
  return (
    <details className="menu-popover">
      <summary className="icon-button" aria-label="Choose table columns" title="Choose columns">
        <Columns3 aria-hidden="true" size={17} />
      </summary>
      <div className="menu-popover__panel menu-popover__panel--right column-menu">
        <div className="filter-menu__heading">
          <strong>Table columns</strong>
          <button className="text-button" type="button" onClick={onReset}>
            Reset
          </button>
        </div>
        {COLUMN_DEFINITIONS.map((column) => (
          <label className="check-option" key={column.key}>
            <input
              type="checkbox"
              checked={visibleColumns.has(column.key)}
              onChange={() => onToggle(column.key)}
            />
            <span className="check-option__box" aria-hidden="true">
              <Check size={12} />
            </span>
            <span>{column.label}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

export function FindingFilters({
  state,
  setState,
  facets,
  total,
  visibleStart,
  visibleEnd,
  advancedOpen,
  setAdvancedOpen,
  visibleColumns,
  onToggleColumn,
  onResetColumns,
  selectedCount,
  exporting,
  onExportSelected,
  onExportFiltered,
  onExportError,
  onClearAll,
  queueBreakdown,
  savedViews,
  onCopyLink,
  onRefresh,
  refreshing = false,
  shareMessage,
}: {
  state: FindingQueryState;
  setState: Dispatch<SetStateAction<FindingQueryState>>;
  facets: FindingFacets | null;
  total: number;
  visibleStart: number;
  visibleEnd: number;
  advancedOpen: boolean;
  setAdvancedOpen: (open: boolean) => void;
  visibleColumns: ReadonlySet<ColumnKey>;
  onToggleColumn: (column: ColumnKey) => void;
  onResetColumns: () => void;
  selectedCount: number;
  exporting: boolean;
  onExportSelected: () => void;
  onExportFiltered: () => void;
  onExportError?: ((error: Error | null) => void) | undefined;
  onClearAll: () => void;
  queueBreakdown?: ReactNode;
  savedViews?: ReactNode;
  onCopyLink?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  shareMessage?: string;
}) {
  const advancedCount = countAdvancedFilters(state);
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const [facetSearch, setFacetSearch] = useState("");
  const [jsonlExporting, setJsonlExporting] = useState(false);
  const [jsonlError, setJsonlError] = useState<string | null>(null);
  const exportBusy = exporting || jsonlExporting;
  const advancedTriggerRef = useRef<HTMLButtonElement>(null);
  const advancedPanelRef = useRef<HTMLElement>(null);
  const mobileDialogProps = mobileDrawer
    ? ({ role: "dialog", "aria-modal": true, tabIndex: -1 } as const)
    : {};
  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const updateViewport = () => setMobileDrawer(media.matches);
    updateViewport();
    media.addEventListener("change", updateViewport);
    return () => media.removeEventListener("change", updateViewport);
  }, []);
  useEffect(() => {
    if (!mobileDrawer || !advancedOpen) return;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => {
      advancedPanelRef.current?.querySelector<HTMLInputElement>("[data-facet-search]")?.focus();
    });
    return () => {
      document.body.style.overflow = priorOverflow;
    };
  }, [advancedOpen, mobileDrawer]);
  const closeAdvanced = () => {
    setAdvancedOpen(false);
    window.requestAnimationFrame(() => advancedTriggerRef.current?.focus());
  };
  const handleDrawerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!mobileDrawer) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closeAdvanced();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [
      ...(advancedPanelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      ) ?? []),
    ].filter((element) => element.offsetParent !== null);
    const first = focusable.at(0);
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const statusOptions = FINDING_STATUSES.map((value) => {
    const liveFacet = facets?.statuses.find((facet) => facet.value === value);
    return {
      value,
      label: liveFacet?.label ?? titleCase(value),
      count: liveFacet?.count ?? 0,
    };
  });
  const analysisFamilyOptions = ANALYSIS_FAMILIES.map((value) => {
    const liveFacet = facets?.analysisFamilies.find((facet) => facet.value === value);
    return {
      value,
      label: ANALYSIS_FAMILY_LABELS[value],
      count: liveFacet?.count ?? 0,
    };
  });
  const severityOptions = SEVERITIES.map((value) => ({
    value,
    count: facets?.severities.find((facet) => facet.value === value)?.count ?? 0,
  }));
  const validationOptions = VALIDATION_DEPTHS.map((value) => ({
    value,
    count: facets?.validationDepths.find((facet) => facet.value === value)?.count ?? 0,
  }));
  const businessTierOptions = BUSINESS_TIERS.map((value) => {
    const liveFacet = facets?.businessTiers.find((facet) => facet.value === value);
    return {
      value,
      label: value === "unknown" ? "Unknown" : value.replace("_", " ").toUpperCase(),
      count: liveFacet?.count ?? 0,
    };
  });
  const enrichmentOptions = ENRICHMENT_STATES.map((value) => {
    const liveFacet = facets?.enrichmentStates.find((facet) => facet.value === value);
    return { value, label: titleCase(value), count: liveFacet?.count ?? 0 };
  });
  const remediationOptions = REMEDIATION_STATES.map((value) => {
    const liveFacet = facets?.remediationStates.find((facet) => facet.value === value);
    return { value, label: titleCase(value), count: liveFacet?.count ?? 0 };
  });
  const privateReadOptions = PRIVATE_READ_STATES.map((value) => {
    const liveFacet = facets?.privateReadStates.find((facet) => facet.value === value);
    return { value, label: liveFacet?.label ?? titleCase(value), count: liveFacet?.count ?? 0 };
  });
  const slaOptions = SLA_STATES.map((value) => {
    const liveFacet = facets?.slaStates?.find((facet) => facet.value === value);
    return { value, label: liveFacet?.label ?? titleCase(value), count: liveFacet?.count ?? 0 };
  });
  const engineOptions =
    facets?.engines.map((facet) => ({
      ...facet,
      label: publicEngineLabel(facet.value),
    })) ?? [];
  const update = (patch: Partial<FindingQueryState>) =>
    setState((current) => ({ ...current, ...patch, page: 1 }));
  const appliedChips = advancedChipItems(state, update);

  const viewToolbar = (
    <div className="view-toolbar" role="toolbar" aria-label="Finding list controls">
      <fieldset className="segmented-control">
        <legend className="sr-only">Finding view</legend>
        <button
          type="button"
          aria-pressed={state.preset === "actionable"}
          onClick={() =>
            update({ preset: "actionable", statuses: statusesForPreset("actionable") })
          }
        >
          <Filter aria-hidden="true" size={14} />
          Actionable
        </button>
        <button
          type="button"
          aria-pressed={state.preset === "all"}
          onClick={() => update({ preset: "all", statuses: statusesForPreset("all") })}
        >
          All findings
        </button>
      </fieldset>
      <span className="row-count" aria-live="polite">
        {total > 0
          ? `${formatInteger(visibleStart)}–${formatInteger(visibleEnd)} of ${formatInteger(total)}`
          : "0 findings"}
      </span>
      <ColumnChooser
        visibleColumns={visibleColumns}
        onToggle={onToggleColumn}
        onReset={onResetColumns}
      />
      <details className="menu-popover export-menu">
        <summary className="control-button" aria-label="Export findings">
          <Download aria-hidden="true" size={15} />
          Export
          <ChevronDown aria-hidden="true" size={14} />
        </summary>
        <div className="menu-popover__panel menu-popover__panel--right">
          <button
            className="menu-item"
            type="button"
            disabled={selectedCount === 0 || exportBusy}
            onClick={(event) => {
              event.currentTarget.closest("details")?.removeAttribute("open");
              onExportSelected();
            }}
          >
            <Download aria-hidden="true" size={15} />
            <span>Selected rows</span>
            <span className="menu-item__meta">{selectedCount}</span>
          </button>
          <button
            className="menu-item"
            type="button"
            disabled={exportBusy}
            onClick={(event) => {
              event.currentTarget.closest("details")?.removeAttribute("open");
              onExportFiltered();
            }}
          >
            <Download aria-hidden="true" size={15} />
            <span>Filtered result</span>
            <span className="menu-item__meta">CSV</span>
          </button>
          <button
            className="menu-item"
            type="button"
            disabled={exportBusy}
            onClick={(event) => {
              event.currentTarget.closest("details")?.removeAttribute("open");
              void (async () => {
                setJsonlExporting(true);
                setJsonlError(null);
                onExportError?.(null);
                try {
                  const result = await api.exportFindingsJsonl({
                    ...filtersForExport(state),
                    page: 1,
                    pageSize: 500,
                  });
                  downloadExport(result);
                } catch (reason) {
                  const error =
                    reason instanceof Error
                      ? reason
                      : new Error("The export could not be prepared.");
                  onExportError?.(error);
                  if (!onExportError) setJsonlError(error.message);
                } finally {
                  setJsonlExporting(false);
                }
              })();
            }}
          >
            <Download aria-hidden="true" size={15} />
            <span>Filtered result</span>
            <span className="menu-item__meta">JSONL</span>
          </button>
        </div>
      </details>
      <button
        className="control-button view-toolbar__clear"
        type="button"
        disabled={!hasAnyFilter(state)}
        onClick={onClearAll}
      >
        <X aria-hidden="true" size={14} />
        Clear filters
      </button>
    </div>
  );

  return (
    <div className="finding-controls">
      <div className="finding-hero">
        {queueBreakdown ? <div className="finding-hero__queue">{queueBreakdown}</div> : null}
        <search className="finding-filter-rail">
          <div className="finding-filter-rail__row finding-filter-rail__row--primary">
            <label className="finding-filter-rail__search">
              <Search aria-hidden="true" size={15} />
              <span className="sr-only">Search findings</span>
              <input
                type="search"
                data-testid="findings-search"
                value={state.query}
                placeholder="Search ID, title, path, rule"
                onChange={(event) => update({ query: event.target.value })}
              />
              {state.query ? (
                <button
                  className="finding-filter-rail__search-clear"
                  type="button"
                  aria-label="Clear search"
                  title="Clear search"
                  onClick={() => update({ query: "" })}
                >
                  <X aria-hidden="true" size={13} />
                </button>
              ) : null}
            </label>

            <div className="finding-filter-rail__actions">
              <CheckboxMenu<FindingStatus>
                label="Status"
                selected={state.statuses}
                options={statusOptions}
                onChange={(statuses) =>
                  update({
                    statuses,
                    preset:
                      statuses.length === 0
                        ? "all"
                        : statuses.length === ACTIONABLE_STATUSES.length &&
                            ACTIONABLE_STATUSES.every((status) => statuses.includes(status))
                          ? "actionable"
                          : "custom",
                  })
                }
              />

              <button
                ref={advancedTriggerRef}
                className="control-button"
                type="button"
                aria-label="More filters"
                aria-expanded={advancedOpen}
                aria-controls="advanced-filters"
                onClick={() => (advancedOpen ? closeAdvanced() : setAdvancedOpen(true))}
              >
                <SlidersHorizontal aria-hidden="true" size={15} />
                Filters
                {advancedCount > 0 ? <span className="count-chip">{advancedCount}</span> : null}
              </button>
            </div>
          </div>

          <div className="finding-filter-rail__row finding-filter-rail__row--taxonomy">
            <fieldset className="finding-filter-rail__families" aria-label="Analysis families">
              <legend className="sr-only">Analysis families</legend>
              <div className="finding-filter-rail__families-scroll">
                {analysisFamilyOptions.map((option) => {
                  const pressed = state.analysisFamilies.includes(option.value);
                  const label =
                    option.value === "runtime_defense_feedback"
                      ? "Runtime"
                      : option.value === "pqc"
                        ? "PQC"
                        : (option.label ?? titleCase(option.value));
                  return (
                    <button
                      className="finding-filter-rail__pill"
                      type="button"
                      aria-pressed={pressed}
                      key={option.value}
                      title={option.label ?? titleCase(option.value)}
                      onClick={() =>
                        update({
                          analysisFamilies: toggleOrderedValue<AnalysisFamily>(
                            state.analysisFamilies,
                            option.value,
                            ANALYSIS_FAMILIES,
                          ),
                        })
                      }
                    >
                      {label}
                      {option.count > 0 ? (
                        <span className="finding-filter-rail__qty">
                          {formatInteger(option.count)}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <span className="finding-filter-rail__rule" aria-hidden="true" />

            <fieldset className="finding-filter-rail__severity" aria-label="Severity">
              <legend className="sr-only">Severity filters</legend>
              {severityOptions.map((option) => (
                <button
                  className={`finding-filter-rail__pill finding-filter-rail__pill--${option.value}`}
                  type="button"
                  aria-pressed={state.severities.includes(option.value)}
                  aria-label={titleCase(option.value)}
                  title={titleCase(option.value)}
                  key={option.value}
                  onClick={() =>
                    update({
                      severities: toggleOrderedValue<Severity>(
                        state.severities,
                        option.value,
                        SEVERITIES,
                      ),
                    })
                  }
                >
                  <span className="finding-filter-rail__dot" aria-hidden="true" />
                  {option.value === "critical"
                    ? "Crit"
                    : option.value === "medium"
                      ? "Med"
                      : titleCase(option.value)}
                  {option.count > 0 ? (
                    <span className="finding-filter-rail__qty">{formatInteger(option.count)}</span>
                  ) : null}
                </button>
              ))}
            </fieldset>
          </div>
          {viewToolbar}
        </search>
      </div>
      {jsonlError ? (
        <p className="inline-alert inline-alert--error" role="alert">
          {jsonlError}
        </p>
      ) : null}

      {advancedOpen ? (
        <>
          {mobileDrawer ? (
            <button
              className="facet-drawer__backdrop"
              type="button"
              aria-label="Close finding filters"
              onClick={closeAdvanced}
            />
          ) : null}
          <section
            ref={advancedPanelRef}
            className={`advanced-filters${mobileDrawer ? " advanced-filters--drawer" : ""}`}
            id="advanced-filters"
            aria-label="Advanced search"
            {...mobileDialogProps}
            onKeyDown={handleDrawerKeyDown}
          >
            <header className="filter-studio__header">
              <div className="filter-studio__identity">
                <strong>Advanced search</strong>
                <span>
                  {advancedCount} applied · {formatInteger(total)} results
                </span>
              </div>
              {savedViews ? <div className="filter-studio__views">{savedViews}</div> : null}
              <label className="facet-search">
                <Search aria-hidden="true" size={15} />
                <span className="sr-only">Search available filter options</span>
                <input
                  data-facet-search
                  type="search"
                  value={facetSearch}
                  placeholder="Search filter options"
                  onChange={(event) => setFacetSearch(event.target.value)}
                />
                {facetSearch ? (
                  <button
                    type="button"
                    aria-label="Clear filter option search"
                    onClick={() => setFacetSearch("")}
                  >
                    <X aria-hidden="true" size={14} />
                  </button>
                ) : null}
              </label>
              <div className="filter-studio__actions">
                {shareMessage ? (
                  <span className="sr-only" aria-live="polite">
                    {shareMessage}
                  </span>
                ) : null}
                {onCopyLink ? (
                  <button className="control-button" type="button" onClick={onCopyLink}>
                    <Link2 aria-hidden="true" size={14} />
                    Copy view link
                  </button>
                ) : null}
                {onRefresh ? (
                  <button
                    className="icon-button"
                    type="button"
                    aria-label="Refresh findings"
                    title="Refresh findings"
                    disabled={refreshing}
                    onClick={onRefresh}
                  >
                    <RefreshCw
                      aria-hidden="true"
                      className={refreshing ? "spin" : undefined}
                      size={17}
                    />
                  </button>
                ) : null}
                {mobileDrawer ? (
                  <button
                    className="icon-button"
                    type="button"
                    aria-label="Close finding filters"
                    onClick={closeAdvanced}
                  >
                    <X aria-hidden="true" size={17} />
                  </button>
                ) : null}
              </div>
            </header>
            {appliedChips.length > 0 ? (
              <section className="filter-studio__chips" aria-label="Applied advanced filters">
                {appliedChips.map((chip) => (
                  <button
                    className="filter-studio__chip"
                    type="button"
                    key={chip.key}
                    onClick={chip.onClear}
                  >
                    {chip.label}
                    <X aria-hidden="true" size={11} />
                  </button>
                ))}
              </section>
            ) : null}
            <div className="filter-studio">
              <FilterStudioColumn title="Scope" hint="Where the finding lives">
                <div className="filter-studio__field">
                  <span className="filter-studio__label">Validation</span>
                  <div className="chip-control">
                    {validationOptions.map((option) => (
                      <button
                        type="button"
                        aria-pressed={state.validationDepths.includes(option.value)}
                        key={option.value}
                        onClick={() =>
                          update({
                            validationDepths: toggleOrderedValue<ValidationStage>(
                              state.validationDepths,
                              option.value,
                              VALIDATION_DEPTHS,
                            ),
                          })
                        }
                      >
                        {option.value}V{" "}
                        <span className="count-chip">{formatInteger(option.count)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <CheckboxMenu
                  label="Repository"
                  selected={state.repositoryIds}
                  options={facets?.repositories ?? []}
                  search={facetSearch}
                  onChange={(repositoryIds) => update({ repositoryIds })}
                />
                <CheckboxMenu<FindingBusinessTier>
                  label="Business tier"
                  selected={state.businessTiers}
                  options={businessTierOptions}
                  search={facetSearch}
                  onChange={(businessTiers) => update({ businessTiers })}
                />
                <CheckboxMenu
                  label="Component"
                  selected={state.componentKeys}
                  options={facets?.components ?? []}
                  search={facetSearch}
                  onChange={(componentKeys) => update({ componentKeys })}
                />
                <CheckboxMenu
                  label="Engine"
                  selected={state.engineIds}
                  options={engineOptions}
                  search={facetSearch}
                  renderValue={publicEngineLabel}
                  onChange={(engineIds) => update({ engineIds })}
                />
              </FilterStudioColumn>
              <FilterStudioColumn title="Workflow" hint="Who owns the next action">
                <CheckboxMenu
                  label="Assignee"
                  selected={state.assigneeIds}
                  options={facets?.assignees ?? []}
                  search={facetSearch}
                  onChange={(assigneeIds) => update({ assigneeIds })}
                />
                <CheckboxMenu<FindingSlaState>
                  label="SLA"
                  selected={state.slaStates}
                  options={slaOptions}
                  search={facetSearch}
                  onChange={(slaStates) => update({ slaStates })}
                />
                <CheckboxMenu<FindingEnrichmentState>
                  label="Enrichment"
                  selected={state.enrichmentStates}
                  options={enrichmentOptions}
                  search={facetSearch}
                  onChange={(enrichmentStates) => update({ enrichmentStates })}
                />
                <CheckboxMenu<FindingRemediationState>
                  label="Remediation"
                  selected={state.remediationStates}
                  options={remediationOptions}
                  search={facetSearch}
                  onChange={(remediationStates) => update({ remediationStates })}
                />
                <CheckboxMenu<FindingPrivateReadState>
                  label="My read state"
                  selected={state.privateReadStates}
                  options={privateReadOptions}
                  search={facetSearch}
                  onChange={(privateReadStates) => update({ privateReadStates })}
                />
                <CheckboxMenu
                  label="My categories"
                  selected={state.privateCategoryKeys}
                  options={facets?.privateCategories ?? []}
                  search={facetSearch}
                  onChange={(privateCategoryKeys) => update({ privateCategoryKeys })}
                />
                <CheckboxMenu<FindingAgeBand>
                  label="Age"
                  selected={state.ageBands}
                  options={
                    facets?.ageBands ??
                    AGE_BANDS.map((value) => ({ value, label: value, count: 0 }))
                  }
                  search={facetSearch}
                  onChange={(ageBands) => update({ ageBands })}
                />
              </FilterStudioColumn>
              <FilterStudioColumn title="Taxonomy" hint="What the finding is">
                <CheckboxMenu
                  label="Rule"
                  selected={state.ruleIds}
                  options={
                    facets?.rules.map((rule) => ({ ...rule, label: displayRuleId(rule.value) })) ??
                    []
                  }
                  search={facetSearch}
                  onChange={(ruleIds) => update({ ruleIds })}
                />
                <CheckboxMenu
                  label="CWE"
                  selected={state.cwes}
                  options={facets?.cwes ?? []}
                  search={facetSearch}
                  onChange={(cwes) => update({ cwes })}
                />
                <CheckboxMenu
                  label="OWASP"
                  selected={state.owaspCategories}
                  options={facets?.owaspCategories ?? []}
                  search={facetSearch}
                  onChange={(owaspCategories) => update({ owaspCategories })}
                />
                <CheckboxMenu
                  label="Language"
                  selected={state.languages}
                  options={facets?.languages ?? []}
                  search={facetSearch}
                  onChange={(languages) => update({ languages })}
                />
              </FilterStudioColumn>
              <FilterStudioColumn title="Evidence" hint="How strongly it is proven">
                <CheckboxMenu<EvidenceRecord["replayStatus"]>
                  label="Replay"
                  selected={state.replayStatuses}
                  options={facets?.replayStatuses ?? staticOptions(REPLAY_STATUSES)}
                  search={facetSearch}
                  onChange={(replayStatuses) => update({ replayStatuses })}
                />
                <label className="confidence-control">
                  <span>
                    Min confidence <strong>{state.minConfidence}%</strong>
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={state.minConfidence}
                    onChange={(event) => update({ minConfidence: Number(event.target.value) })}
                  />
                </label>
              </FilterStudioColumn>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
